import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Plus, Save, Trash2, X, ShieldAlert, Lock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

const EMPTY = {
  title: '', description: '', start_date: '', end_date: '', venue: '', city: '',
  poster_image: '', ticket_link: '', ticket_price: '', category: '', status: 'draft',
};

const statusBadge = (s) => {
  if (s === 'published') return <Badge className="bg-green-600 capitalize">{s}</Badge>;
  if (s === 'archived') return <Badge variant="outline" className="capitalize">{s}</Badge>;
  return <Badge variant="secondary" className="capitalize">{s || 'draft'}</Badge>;
};

export default function OrganizerEvents() {
  const [role, setRole] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(api.isAuthenticated());
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const refreshEvents = async () => {
    const list = await api.get('/users/me/organizer/events').catch(() => []);
    setEvents(Array.isArray(list) ? list : []);
  };

  const load = async () => {
    if (!api.isAuthenticated()) { setAuthed(false); setLoading(false); return; }
    try {
      const me = await api.get('/users/me');
      setRole(me.role);
      if (me.role === 'organizer' || me.role === 'admin') {
        try { await api.get('/users/me/organizer'); setHasProfile(true); }
        catch (_) { setHasProfile(false); }
        await refreshEvents();
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (ev) => {
    setEditingId(ev.id || ev.event_id);
    setForm({
      title: ev.title || '', description: ev.description || '', start_date: ev.start_date || '',
      end_date: ev.end_date || '', venue: ev.venue || '', city: ev.city || '',
      poster_image: ev.poster_image || '', ticket_link: ev.ticket_link || '',
      ticket_price: ev.ticket_price ?? '', category: ev.category || '', status: ev.status || 'draft',
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.ticket_price = payload.ticket_price === '' ? null : Number(payload.ticket_price);
      if (editingId) {
        await api.put(`/organizer/events/${editingId}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/organizer/events', payload);
        toast.success('Event created');
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditingId(null);
      await refreshEvents();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? (kept as archived if it has saves or a lineup)`)) return;
    try {
      const res = await api.del(`/organizer/events/${ev.id || ev.event_id}`);
      toast.success(res?.archived ? 'Event archived (had related records)' : 'Event deleted');
      await refreshEvents();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  if (!authed) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
        <p className="mb-4">Please log in to manage your events.</p>
        <Link to="/login"><Button>Go to Login</Button></Link>
      </div>
    );
  }
  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16 text-center opacity-70">Loading…</div>;

  if (role !== 'organizer' && role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />
        <h1 className="text-xl font-semibold mb-2">Organizer access required</h1>
        <p className="opacity-80">Ask an administrator to grant you the <span className="font-medium">organizer</span> role to manage events.</p>
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <Building2Fallback />
        <h1 className="text-xl font-semibold mb-2">Create your organizer profile first</h1>
        <p className="opacity-80 mb-4">You need an organizer profile before creating events.</p>
        <Link to="/organizer/profile"><Button>Go to Organizer Profile</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="h-6 w-6" /> My Events</h1>
        {!showForm && <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> New event</Button>}
      </div>

      <div className="mb-6 rounded-md border border-blue-400/40 bg-blue-50 dark:bg-blue-950/30 p-3 text-sm text-blue-700 dark:text-blue-300 flex gap-2">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Your event appears publicly only after your organizer profile is verified and the event is published.</span>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader><CardTitle>{editingId ? 'Edit event' : 'New event'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input id="title" value={form.title} onChange={(e) => set('title', e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="desc">Description</Label>
                <Textarea id="desc" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label htmlFor="start">Start (ISO)</Label><Input id="start" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} placeholder="2026-09-01T20:00:00Z" /></div>
                <div><Label htmlFor="end">End (ISO)</Label><Input id="end" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} placeholder="2026-09-02T01:00:00Z" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label htmlFor="venue">Venue</Label><Input id="venue" value={form.venue} onChange={(e) => set('venue', e.target.value)} /></div>
                <div><Label htmlFor="city">City</Label><Input id="city" value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label htmlFor="poster">Poster image URL</Label><Input id="poster" value={form.poster_image} onChange={(e) => set('poster_image', e.target.value)} /></div>
                <div><Label htmlFor="tlink">Ticket link</Label><Input id="tlink" value={form.ticket_link} onChange={(e) => set('ticket_link', e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div><Label htmlFor="price">Ticket price</Label><Input id="price" type="number" step="0.01" value={form.ticket_price} onChange={(e) => set('ticket_price', e.target.value)} /></div>
                <div><Label htmlFor="cat">Category</Label><Input id="cat" value={form.category} onChange={(e) => set('category', e.target.value)} /></div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select id="status" value={form.status} onChange={(e) => set('status', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <option value="draft">draft</option>
                    <option value="published">published</option>
                    <option value="archived">archived</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button type="submit" disabled={busy}><Save className="h-4 w-4 mr-2" />{busy ? 'Saving…' : (editingId ? 'Save event' : 'Create event')}</Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}><X className="h-4 w-4 mr-2" /> Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {events.length === 0 && <p className="opacity-60 text-center py-8">No events yet.</p>}
        {events.map((ev) => (
          <Card key={ev.id || ev.event_id}>
            <CardContent className="py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{ev.title || '(untitled)'}</span>
                  {statusBadge(ev.status)}
                </div>
                <div className="text-sm opacity-70 truncate">
                  {[ev.city, ev.venue, ev.start_date].filter(Boolean).join(' · ') || 'No details'}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => openEdit(ev)}>Edit</Button>
                <Button variant="outline" size="sm" onClick={() => remove(ev)} className="text-red-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Building2Fallback() {
  return <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />;
}
